import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PanelLeftClose, PanelLeftOpen, Search, Shield, Star, LogIn, LogOut, User, Trophy, Package, Settings2, ChevronDown, Menu, Tv, Sprout, Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import NotificationBell from "./NotificationBell";
import SeedWalletBadge from "./SeedWalletBadge";
import ServerStatusBadge from "./ServerStatusBadge";
import LiveStreamers from "./LiveStreamers";
import { DogTag } from "./Cosmetics";
import { FOUNDERS_ENABLED } from "../lib/flags";

const TopBar = ({ collapsed, onToggle, onMobileMenu }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [isStreamer, setIsStreamer] = useState(false);

  useEffect(() => {
    if (!user) { setIsStreamer(false); return; }
    api.get("/streamer/me").then((r) => setIsStreamer(!!r.data.is_streamer)).catch(() => setIsStreamer(false));
  }, [user]);

  const openPalette = () => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));

  return (
    <header data-testid="top-bar" className="fixed top-0 inset-x-0 h-16 z-50 glass-dark border-b border-[#D4A94A]/15 flex items-center px-3 lg:px-4 gap-3">
      <button data-testid="mobile-menu-toggle" onClick={onMobileMenu} className="lg:hidden w-9 h-9 flex items-center justify-center text-[#94A3B8] hover:text-[#F1F5F9]" aria-label="Open menu"><Menu className="w-5 h-5" /></button>
      <button data-testid="sidebar-toggle-button" onClick={onToggle} className="hidden lg:flex w-9 h-9 items-center justify-center text-[#94A3B8] hover:text-[#D4A94A] hover:bg-[#13151B] tactical-frame-sm transition-colors" aria-label="Toggle sidebar">
        {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
      </button>

      <Link to="/" data-testid="topbar-logo" className="flex items-center gap-2.5 shrink-0 group">
        <img src="/oce-dogs-logo.webp" alt="OCE DOGS" className="h-10 w-auto shrink-0 drop-shadow-[0_0_10px_rgba(212,169,74,0.25)] group-hover:drop-shadow-[0_0_14px_rgba(212,169,74,0.45)] transition-all" />
        <div className="hidden sm:flex flex-col leading-none">
          <span className="font-heading font-black text-base tracking-tight uppercase">OCE Dogs</span>
          <span className="font-mono text-[8px] tracking-[0.3em] text-[#D4A94A]">OCE // AU · NZ</span>
        </div>
      </Link>

      <button data-testid="global-search-input" onClick={openPalette} className="ml-2 lg:ml-6 flex-1 min-w-0 max-w-xl h-10 inline-flex items-center gap-3 px-3 bg-[#13151B]/80 border border-[#2A2E3D] hover:border-[#D4A94A]/60 focus:border-[#D4A94A] tactical-frame-sm text-sm text-[#64748B] transition-colors">
        <Search className="w-4 h-4 text-[#94A3B8]" />
        <span className="flex-1 text-left truncate">Search operators, clans, weapons, guides…</span>
        <span className="hidden md:inline font-mono text-[10px] text-[#64748B] border border-[#2A2E3D] px-1.5 py-0.5">Ctrl K</span>
      </button>

      <div className="ml-auto flex items-center gap-2">
        {user?.is_patron ? (
          <Link to="/vip" data-testid="vip-member-badge" title={user.patron_tier ? `VIP · ${user.patron_tier}` : "VIP member"}
            className="vip-chip hidden md:inline-flex items-center gap-1.5 h-9 px-3 font-stencil font-black uppercase text-xs tactical-frame-sm">
            <Crown className="w-3.5 h-3.5" /> VIP{user.patron_tier ? ` · ${user.patron_tier}` : ""}
          </Link>
        ) : (
          <Link to="/patreon" data-testid="vip-subscribe-button" title="Unlock VIP perks — reserved slot, badge & more"
            className="inline-flex items-center gap-1.5 h-9 px-2.5 sm:px-3.5 bg-[#F96854] hover:bg-[#fa7d6c] text-white font-stencil font-black uppercase text-xs tactical-frame-sm transition-all shadow-[0_0_16px_rgba(249,104,84,0.4)] hover:shadow-[0_0_24px_rgba(249,104,84,0.6)]">
            <Crown className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Subscribe to VIP</span><span className="sm:hidden">VIP</span>
          </Link>
        )}
        <LiveStreamers />
        <ServerStatusBadge />
        <SeedWalletBadge user={user} />
        <NotificationBell user={user} />
        {user?.is_supporter ? (
          <span data-testid="founder-badge" className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 border border-[#D4A94A]/60 text-[#D4A94A] font-stencil font-bold uppercase text-xs tactical-frame-sm">
            <Star className="w-3.5 h-3.5 fill-[#D4A94A]" /> Founder
          </span>
        ) : FOUNDERS_ENABLED ? (
          <Link to="/supporter" data-testid="become-founder-button" className="hidden md:inline-flex items-center gap-1.5 h-9 px-3.5 bg-[#D4A94A] hover:bg-[#E5BD60] text-black font-stencil font-black uppercase text-xs tactical-frame-sm transition-colors">
            <Star className="w-3.5 h-3.5" /> Founder
          </Link>
        ) : null}

        {user ? (
          <div className="relative">
            <button data-testid="account-menu-button" onClick={() => setMenu((m) => !m)} className={`h-10 inline-flex items-center gap-2 pl-1 pr-2 bg-[#13151B]/80 border tactical-frame-sm transition-colors ${user.is_patron ? "vip-panel" : "border-[#2A2E3D] hover:border-[#D4A94A]/60"}`}>
              <DogTag frameId={user.equipped_frame} avatarUrl={user.avatar_url} callsign={user.callsign} size={30} isSupporter={user.is_supporter} vip={user.is_patron} />
              <div className="hidden md:flex flex-col leading-tight text-left">
                <span className={`font-stencil font-bold text-xs uppercase max-w-[120px] truncate ${user.is_patron ? "vip-name" : ""}`}>{user.display_name || user.callsign}</span>
                <span className="font-mono text-[9px] text-[#D4A94A]">LVL {user.stats?.level ?? 1}</span>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 text-[#94A3B8] transition-transform ${menu ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {menu && (
                <>
                  <div className="fixed inset-0 z-[45]" onClick={() => setMenu(false)} />
                  <motion.div initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }} transition={{ duration: 0.16 }}
                    data-testid="account-menu" className={`!absolute right-0 top-full mt-2 w-56 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5rem)] overflow-y-auto origin-top-right glass tactical-frame z-50 p-1.5 ${user.is_patron ? "vip-panel" : ""}`}>
                    {[
                      ...(user.is_patron ? [{ to: "/vip", label: "VIP Lounge", icon: Crown, id: "vip-lounge" }] : []),
                      { to: "/profile", label: "Profile", icon: User, id: "profile" },
                      { to: "/seeding", label: "Seed Level", icon: Sprout, id: "seeding" },
                      { to: "/season", label: "Season Pass", icon: Trophy, id: "season" },
                      { to: "/market", label: "Inventory & Market", icon: Package, id: "market" },
                      ...(isStreamer ? [{ to: "/streamer/studio", label: "Streamer Studio", icon: Tv, id: "streamer-studio" }] : []),
                      ...(user.role === "admin" || user.role === "moderator" ? [{ to: "/admin", label: user.role === "admin" ? "Admin" : "Mod Tools", icon: Settings2, id: "admin" }] : []),
                    ].map((i) => (
                      <Link key={i.to} to={i.to} data-testid={`account-menu-${i.id}`} onClick={() => setMenu(false)} className="flex items-center gap-2.5 px-3 py-2 text-sm text-[#CBD5E1] hover:text-[#F1F5F9] hover:bg-[#1A1D26] font-stencil font-bold uppercase tracking-wide">
                        <i.icon className="w-4 h-4 text-[#D4A94A]" /> {i.label}
                      </Link>
                    ))}
                    <div className="my-1 border-t border-[#2A2E3D]" />
                    <button data-testid="account-menu-logout" onClick={() => { logout(); setMenu(false); navigate("/"); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#1A1D26] font-stencil font-bold uppercase tracking-wide">
                      <LogOut className="w-4 h-4" /> Log out
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <button data-testid="sign-in-button" onClick={() => navigate("/auth?mode=login")} className="h-10 inline-flex items-center gap-2 px-4 bg-[#13151B]/80 border border-[#2A2E3D] hover:border-[#D4A94A] text-[#F1F5F9] font-stencil font-bold uppercase text-sm tactical-frame-sm transition-colors">
            <LogIn className="w-4 h-4" /> <span className="hidden sm:inline">Sign In</span>
          </button>
        )}
      </div>
    </header>
  );
};

export default TopBar;
